import React, { useEffect, useState, useContext } from 'react';
import { AdminContext } from '../AdminContext';

export default function CeremonyRegistrations() {
  const { token } = useContext(AdminContext);
  const [ceremonyRegistrations, setCeremonyRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter ceremony registrations based on search term
  const filteredRegistrations = ceremonyRegistrations.filter(reg =>
    reg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.organization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.interest?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.gender?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort ceremony registrations
  const sortedRegistrations = [...filteredRegistrations].sort((a, b) => {
    let aVal = a[sortConfig.key] || '';
    let bVal = b[sortConfig.key] || '';
    
    // Handle dates
    if (sortConfig.key === 'created_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (sortConfig.direction === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedRegistrations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRegistrations = sortedRegistrations.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  useEffect(() => {
    async function fetchCeremonyRegistrations() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('/.netlify/functions/admin-list-ceremony', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        let data;
        try {
          data = await res.json();
        } catch (jsonErr) {
          // Handle non-JSON responses (like HTML error pages)
          const text = await res.text();
          if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
            throw new Error('Server returned HTML instead of JSON. Please check if the backend is running correctly.');
          }
          throw new Error('Invalid JSON response from server');
        }
        
        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch ceremony registrations');
        }
        
        setCeremonyRegistrations(data.ceremony_registrations || []);
      } catch (err) {
        console.error('Error fetching ceremony registrations:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    if (token) fetchCeremonyRegistrations();
  }, [token]);

  return (
    <div style={{ maxWidth: '100%', margin: '40px auto', padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 16 }}>Closing Ceremony Registrations</h2>
        
        {/* Search and Stats */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search ceremony registrations..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: 6,
                width: 250,
                fontSize: '14px'
              }}
            />
            <span style={{ color: '#666', fontSize: '14px' }}>
              Showing {paginatedRegistrations.length} of {filteredRegistrations.length} registrations
            </span>
          </div>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}
      {error && <div style={{ color: 'red', padding: '10px', backgroundColor: '#ffebee', borderRadius: 4, marginBottom: 20 }}>{error}</div>}
      
      {!loading && !error && (
        <div style={{ background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {paginatedRegistrations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {filteredRegistrations.length === 0 && searchTerm 
                ? `No ceremony registrations found matching "${searchTerm}"`
                : 'No ceremony registrations found.'
              }
            </div>
          ) : (
            <>
              {/* Enhanced Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th 
                        onClick={() => handleSort('name')}
                        style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left', 
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Full Name {getSortIcon('name')}
                      </th>
                      <th 
                        onClick={() => handleSort('email')}
                        style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left', 
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Email {getSortIcon('email')}
                      </th>
                      <th 
                        onClick={() => handleSort('phone')}
                        style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left', 
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Phone {getSortIcon('phone')}
                      </th>
                      <th 
                        onClick={() => handleSort('gender')}
                        style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left', 
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Gender {getSortIcon('gender')}
                      </th>
                      <th 
                        onClick={() => handleSort('organization')}
                        style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left', 
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Organization {getSortIcon('organization')}
                      </th>
                      <th 
                        onClick={() => handleSort('interest')}
                        style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left', 
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Interest {getSortIcon('interest')}
                      </th>
                      <th 
                        onClick={() => handleSort('created_at')}
                        style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left', 
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Registered {getSortIcon('created_at')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRegistrations.map((reg, index) => (
                      <tr 
                        key={reg.id} 
                        style={{ 
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e3f2fd'}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = index % 2 === 0 ? '#fafafa' : 'white';
                        }}
                      >
                        <td style={{ padding: '12px 16px', fontWeight: 500 }}>
                          {reg.name || 'N/A'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#006400', fontWeight: 500 }}>
                          {reg.email}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {reg.phone || 'N/A'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {reg.gender && (
                            <span style={{ 
                              padding: '4px 8px', 
                              borderRadius: 12, 
                              backgroundColor: reg.gender === 'male' ? '#e3f2fd' : '#fce4ec',
                              color: reg.gender === 'male' ? '#1565c0' : '#c2185b',
                              fontSize: '12px',
                              fontWeight: 600
                            }}>
                              {reg.gender}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {reg.organization || 'N/A'}
                        </td>
                        <td style={{ padding: '12px 16px', maxWidth: 200 }}>
                          <div style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {reg.interest || 'N/A'}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#666', fontSize: '14px' }}>
                          {reg.created_at ? new Date(reg.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  padding: '20px',
                  borderTop: '1px solid #f0f0f0',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      background: currentPage === 1 ? '#f5f5f5' : 'white',
                      color: currentPage === 1 ? '#999' : '#333',
                      borderRadius: 4,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Previous
                  </button>
                  
                  <span style={{ padding: '0 16px', color: '#666', fontSize: '14px' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      background: currentPage === totalPages ? '#f5f5f5' : 'white',
                      color: currentPage === totalPages ? '#999' : '#333',
                      borderRadius: 4,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}